import axios from 'axios';
import { getApiUrl } from '../config/environment';

const api = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authentication interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface DomainAvailability {
  domainName: string;
  available: boolean;
  price?: number;
  premium?: boolean;
  transferable?: boolean;
}

export interface DomainSuggestion {
  domainName: string;
  available: boolean;
  price: number;
}

export interface DomainSearchResult {
  searchTerm: string;
  results: DomainAvailability[];
  suggestions: DomainSuggestion[];
}

export interface Domain {
  id: string;
  customer_id: string;
  domain_name: string;
  registrar?: string;
  registration_date: string;
  expiration_date: string;
  auto_renew: boolean;
  status: 'active' | 'pending' | 'expired' | 'suspended';
  nameservers?: string[];
  dns_settings?: any;
  privacy_protection: boolean;
  price: number;
  created_at: string;
  updated_at: string;
  // Fields from joined customer/user data (for admin view)
  company_name?: string;
  first_name?: string;
  last_name?: string;
  customer_email?: string;
}

export interface ContactInfo {
  firstName: string;
  lastName: string;
  contactType?: 'PERSON' | 'COMPANY' | 'ASSOCIATION' | 'PUBLIC_BODY' | 'RESELLER';
  organizationName?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state?: string;
  countryCode: string;
  zipCode: string;
  phoneNumber: string;
  email: string;
}

export interface DomainRegistrationData {
  domainName: string;
  durationInYears?: number;
  contactInfo: ContactInfo;
  privacyProtection?: boolean;
  autoRenew?: boolean;
}

export interface DomainTransferData {
  domainName: string;
  authCode: string;
  contactInfo: ContactInfo;
}

export const domainService = {
  async searchDomains(searchTerm: string, tlds?: string[]): Promise<ApiResponse<DomainSearchResult>> {
    try {
      const params = new URLSearchParams({ q: searchTerm });
      if (tlds && tlds.length > 0) {
        tlds.forEach(tld => params.append('tlds', tld));
      }

      const url = getApiUrl(`/domains/search?${params.toString()}`);
      console.log('🌐 Domain API request URL:', url);
      
      // Test basic connectivity first
      try {
        console.log('🔌 Testing basic connectivity...');
        await api.get(getApiUrl('/health'));
        console.log('✅ Backend connectivity confirmed');
      } catch (healthError) {
        console.error('❌ Backend connectivity failed:', healthError);
        throw new Error('Cannot connect to backend server. Please ensure it is running on port 5001.');
      }
      
      const response = await api.get(url);
      console.log('📨 Domain API response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('🚨 Domain API error:', error);
      console.error('Error message:', error.message);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      console.error('Request config:', error.config);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to search domains',
      };
    }
  },

  async checkDomainAvailability(domain: string): Promise<ApiResponse<DomainAvailability>> {
    try {
      const response = await api.get(getApiUrl(`/domains/check/${encodeURIComponent(domain)}`));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to check domain availability',
      };
    }
  },

  async registerDomain(data: DomainRegistrationData): Promise<ApiResponse> {
    try {
      const response = await api.post(getApiUrl('/domains/register'), data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to register domain',
      };
    }
  },

  async transferDomain(data: DomainTransferData): Promise<ApiResponse> {
    try {
      const response = await api.post(getApiUrl('/domains/transfer'), data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to transfer domain',
      };
    }
  },

  async getMyDomains(): Promise<ApiResponse<Domain[]>> {
    try {
      const response = await api.get(getApiUrl('/domains/my-domains'));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get domains',
      };
    }
  },

  async getDomain(id: string): Promise<ApiResponse<Domain>> {
    try {
      const response = await api.get(getApiUrl(`/domains/${id}`));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get domain',
      };
    }
  },

  async updateNameservers(id: string, nameservers: string[]): Promise<ApiResponse> {
    try {
      const response = await api.put(getApiUrl(`/domains/${id}/nameservers`), { nameservers });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update nameservers',
      };
    }
  },

  async toggleAutoRenew(id: string): Promise<ApiResponse> {
    try {
      const response = await api.put(getApiUrl(`/domains/${id}/auto-renew`));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to toggle auto-renew',
      };
    }
  },

  async togglePrivacyProtection(id: string): Promise<ApiResponse> {
    try {
      const response = await api.put(getApiUrl(`/domains/${id}/privacy`));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to toggle privacy protection',
      };
    }
  },

  async getDomainStats(): Promise<ApiResponse> {
    try {
      const response = await api.get(getApiUrl('/domains/stats'));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get domain statistics',
      };
    }
  },

  // Admin methods
  async getAllDomainsAdmin(opts: { page?: number; limit?: number } = {}): Promise<ApiResponse> {
    try {
      const params = new URLSearchParams();
      if (opts.page) params.set('page', String(opts.page));
      if (opts.limit) params.set('limit', String(opts.limit));
      const qs = params.toString();
      const response = await api.get(getApiUrl(`/domains/admin/all${qs ? `?${qs}` : ''}`));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch domains',
      };
    }
  },

  async updateDomainAdmin(domainId: string, updateData: any): Promise<ApiResponse> {
    try {
      const response = await api.put(getApiUrl(`/domains/admin/${domainId}`), updateData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update domain',
      };
    }
  },

  async deleteDomainAdmin(domainId: string): Promise<ApiResponse> {
    try {
      const response = await api.delete(getApiUrl(`/domains/admin/${domainId}`));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete domain',
      };
    }
  },

  async renewDomainAdmin(domainId: string, years: number = 1): Promise<ApiResponse> {
    try {
      const response = await api.post(getApiUrl(`/domains/admin/${domainId}/renew`), { years });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to renew domain',
      };
    }
  },

  async getDomainStatsAdmin(): Promise<ApiResponse> {
    try {
      const response = await api.get(getApiUrl('/domains/admin/stats'));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch domain stats',
      };
    }
  },

  async syncAwsDomainsAdmin(): Promise<ApiResponse> {
    try {
      const response = await api.post(getApiUrl('/domains/admin/sync-aws'));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to sync AWS domains',
      };
    }
  },

  // P4: post-payment provisioning poll
  async getProvisioningStatus(orderId: string): Promise<ApiResponse> {
    try {
      const response = await api.get(getApiUrl(`/domains/provisioning/order/${orderId}`));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch provisioning status',
      };
    }
  },

  // P6: admin billing reconciliation
  async getDomainBillingReconciliationAdmin(): Promise<ApiResponse> {
    try {
      const response = await api.get(getApiUrl('/domains/admin/billing-reconciliation'));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch reconciliation data',
      };
    }
  },

  // P5: admin provisioning attempts list + retry
  async listProvisioningAttemptsAdmin(status: 'needs_attention' | 'all' | 'awaiting_admin' | 'failed' | 'completed' | 'in_progress' = 'needs_attention'): Promise<ApiResponse> {
    try {
      const response = await api.get(getApiUrl(`/domains/admin/provisioning-attempts?status=${encodeURIComponent(status)}`));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to list provisioning attempts',
      };
    }
  },

  async retryProvisioningAdmin(attemptId: string): Promise<ApiResponse> {
    try {
      const response = await api.post(getApiUrl(`/domains/admin/provisioning-attempts/${attemptId}/retry`));
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retry provisioning attempt',
      };
    }
  },
};